import { useMutation } from "@tanstack/react-query";
import { SubmitHandler, useForm } from "react-hook-form";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

import type { PasswordReset } from "~/api/Api";
import { apiClient } from "~/api/ApiClient";
import FormTitle from "~/components/login/formTitle/FormTitle";
import PasswordInput from "~/components/login/passwordInput/PasswordInput";
import Button from "~/components/ui/button/Button";
import FormBaseErrors from "~/components/ui/formBaseErrors/FormBaseErrors";
import { PAGE_TITLES } from "~/configs/pageTitles";
import labels from "~/helpers/labels";
import { tryCatchParsed } from "~/helpers/tryCatch";
import { useFormErrors } from "~/hooks/useFormErrors";
import { usePageTitle } from "~/hooks/usePageTitle";
import { routes } from "~/router";

import styles from "./Login.module.scss";

const CreateNewPasswordPage = () => {
  usePageTitle(PAGE_TITLES.createNewPassword);

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
    getValues,
    setError,
  } = useForm<PasswordReset>();
  const { baseErrors, applyServerErrors } = useFormErrors(setError, [
    "reset_password_token",
    "email",
  ]);

  const { mutateAsync, isPending } = useMutation({
    mutationFn: (data: PasswordReset) =>
      apiClient.password.passwordPartialUpdate(data),
  });

  const onSubmit: SubmitHandler<PasswordReset> = async (data) => {
    const result = await tryCatchParsed(
      mutateAsync({ ...data, reset_password_token: token }),
    );

    if (result.error) {
      applyServerErrors(result.error);

      return;
    }

    reset();
    navigate(routes.auth.login());
  };

  return (
    <>
      <FormTitle title={labels.CREATE_NEW_PASSWORD} />
      <FormBaseErrors errors={baseErrors} />
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className={styles.inputsWrapper}>
          <PasswordInput
            passwordError={errors.password?.message}
            register={register}
            label={labels.NEW_PASSWORD}
            field="password"
            getValues={getValues}
            disabled={isPending}
          />
        </div>
        <div className={styles.inputsWrapper}>
          <PasswordInput
            passwordError={errors.password_confirmation?.message}
            register={register}
            label={labels.CONFIRM_PASSWORD}
            field="password_confirmation"
            getValues={getValues}
            disabled={isPending}
          />
        </div>
        <Button
          fullWidth
          type="submit"
          disabled={isPending}
          withSpinner={isPending}
        >
          {labels.RESET_PASSWORD}
        </Button>
      </form>
      <div className={styles.footer}>
        <div className={styles.footerText}>{labels.KNOW_YOUR_PASSWORD}</div>
        <Link className={styles.footerLink} to={routes.auth.login()}>
          {labels.BACK_TO_LOGIN}
        </Link>
      </div>
    </>
  );
};
export default CreateNewPasswordPage;
